import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import { useRewardPhotos } from '../hooks/useGrowth';
import { RewardChip } from './RewardChip';

/** The guide's own submissions, with status and any rejection reason. */
export function PhotosPanel({ onAddTravellerPhoto }: { onAddTravellerPhoto: () => void }) {
  const { t } = useTranslation('rewards');
  const { data } = useRewardPhotos();

  return (
    <Box>
      <DashboardSectionHeader
        title={t('photo.mine')}
        action={
          <Button startIcon={<AddAPhotoOutlinedIcon />} onClick={onAddTravellerPhoto} sx={{ borderRadius: 999, fontWeight: 700 }}>
            {t('earn.actions.UPLOAD_TRAVELLER_PHOTO')}
          </Button>
        }
      />
      <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 2 }}>
        {!data?.length ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {t('photo.empty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {data.map((photo) => (
              <Box key={photo.id} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Box component="img" src={photo.url} alt="" loading="lazy" sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', bgcolor: 'grey.100' }} />
                <Box sx={{ p: 1, display: 'grid', gap: 0.5 }}>
                  <RewardChip label={t(`status.photo.${photo.status}`)} status={photo.status} />
                  <Typography variant="caption" color="text.secondary">
                    {photo.type === 'T_SHIRT_SELFIE' ? t('photo.selfieTitle') : t('photo.manage')} · {dayjs(photo.createdAt).format('D MMM')}
                  </Typography>
                  {photo.rejectionReason && (
                    <Typography variant="caption" color="error.main" sx={{ lineHeight: 1.35 }}>
                      {photo.rejectionReason}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
